import { useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  TextInput,
  Button,
  Text,
  Divider,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { participantsApi, type CreateParticipantRequest, type CohortCustomFieldDefinition } from '../../../services/api/participants';

interface AddParticipantModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  partnerName?: string;
  customFieldDefinitions?: CohortCustomFieldDefinition[];
  onParticipantAdded: () => void;
}

interface FormValues extends CreateParticipantRequest {
  custom_fields: Record<string, string>;
}

export function AddParticipantModal({
  opened,
  onClose,
  cohortId,
  partnerName,
  customFieldDefinitions = [],
  onParticipantAdded,
}: AddParticipantModalProps) {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    initialValues: {
      first_name: '',
      last_name: '',
      preferred_name: '',
      email: '',
      role: '',
      business_unit: '',
      location: '',
      personality_type: '',
      custom_fields: {},
    },
    validate: {
      first_name: (value) => (value.trim() ? null : 'First name is required'),
      last_name: (value) => (value.trim() ? null : 'Last name is required'),
      email: (value) => {
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email format';
        return null;
      },
    },
  });

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const request: CreateParticipantRequest = {
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        preferred_name: values.preferred_name?.trim() || undefined,
        email: values.email.trim().toLowerCase(),
        role: values.role?.trim() || undefined,
        business_unit: values.business_unit?.trim() || undefined,
        location: values.location?.trim() || undefined,
        personality_type: values.personality_type?.trim() || undefined,
        custom_fields: Object.keys(values.custom_fields).length > 0 ? values.custom_fields : undefined,
      };

      const result = await participantsApi.create(cohortId, request);

      notifications.show({
        title: 'Participant Added',
        message: result.userCreated
          ? `${values.first_name} ${values.last_name} has been added to the cohort (new user created)`
          : `${values.first_name} ${values.last_name} has been enrolled in the cohort`,
        color: 'green',
      });

      form.reset();
      onParticipantAdded();
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add participant';
      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={<Text fw={600} size="lg">Add Participant</Text>}
      size="md"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Required Fields */}
          <Group grow align="flex-start">
            <TextInput
              label="First Name"
              placeholder="Enter first name"
              required
              {...form.getInputProps('first_name')}
            />
            <TextInput
              label="Last Name"
              placeholder="Enter last name"
              required
              {...form.getInputProps('last_name')}
            />
          </Group>

          <Group grow align="flex-start">
            <TextInput
              label="Preferred Name"
              placeholder="Nickname (optional)"
              {...form.getInputProps('preferred_name')}
            />
            <TextInput
              label="Email"
              placeholder="Enter email address"
              required
              {...form.getInputProps('email')}
            />
          </Group>

          <Divider my="xs" />

          {/* Optional Fields */}
          <Text size="sm" c="dimmed" fw={500}>Optional Information</Text>

          {/* Company (read-only from partner) */}
          {partnerName && (
            <TextInput
              label="Company"
              value={partnerName}
              disabled
              styles={{
                input: { cursor: 'not-allowed', backgroundColor: 'var(--mantine-color-gray-1)' },
              }}
            />
          )}

          <Group grow>
            <TextInput
              label="Role"
              placeholder="Job role"
              {...form.getInputProps('role')}
            />
            <TextInput
              label="Business Unit"
              placeholder="Business unit/opco"
              {...form.getInputProps('business_unit')}
            />
          </Group>

          <Group grow>
            <TextInput
              label="Location"
              placeholder="City or Remote"
              {...form.getInputProps('location')}
            />
            <TextInput
              label="Personality Type"
              placeholder="e.g., INTJ, ENFP"
              {...form.getInputProps('personality_type')}
            />
          </Group>

          {/* Custom Fields */}
          {customFieldDefinitions.length > 0 && (
            <>
              <Divider my="xs" />
              <Text size="sm" c="dimmed" fw={500}>Custom Fields</Text>
              {customFieldDefinitions.map((field) => (
                <TextInput
                  key={field.id}
                  label={field.field_label}
                  placeholder={`Enter ${field.field_label.toLowerCase()}`}
                  value={form.values.custom_fields[field.field_name] || ''}
                  onChange={(e) => {
                    form.setFieldValue('custom_fields', {
                      ...form.values.custom_fields,
                      [field.field_name]: e.target.value,
                    });
                  }}
                />
              ))}
            </>
          )}

          <Divider my="xs" />

          {/* Action Buttons */}
          <Group justify="flex-end" gap="sm" className="modal-footer">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" color="medleyGreen" loading={submitting}>
              Add Participant
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export default AddParticipantModal;
